import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

// FEAT-058 (MVP-18.55) — Client UX for default contact-info blocking.
// Messaging is meant to stay on Careva, so the API redacts email/phone/URL
// from outbound messages and reports it via `meta.contactInfoRedacted`. These
// components render a calm, elderly-friendly notice and display the server's
// `[contact info removed]` placeholder cleanly inside the thread.

export const CONTACT_INFO_PLACEHOLDER = '[contact info removed]';

const NOTICE_TEXT =
  'Contact info was removed — keep conversations on Careva for your safety.';

export interface ContactInfoRedactedNoticeProps {
  /** When false the notice renders nothing. */
  visible: boolean;
}

/**
 * A non-alarming, informational banner shown near a just-sent message when the
 * server redacted contact info from it.
 */
export const ContactInfoRedactedNotice: React.FC<ContactInfoRedactedNoticeProps> = ({
  visible,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <Alert
      severity="info"
      icon={<ShieldOutlinedIcon fontSize="inherit" />}
      data-testid="contact-info-redacted-notice"
      role="status"
      aria-live="polite"
      sx={{
        mb: 2,
        borderRadius: 2,
        alignItems: 'center',
        fontSize: '1rem',
      }}
    >
      {NOTICE_TEXT}
    </Alert>
  );
};

export interface RedactedMessageContentProps {
  content: string;
  /** Forwarded to the surrounding Typography for theme-aware coloring. */
  color?: string;
}

/**
 * Renders a message body, styling any `[contact info removed]` placeholder as a
 * subtle inline chip so redactions read clearly instead of looking like the
 * sender typed brackets.
 */
export const RedactedMessageContent: React.FC<RedactedMessageContentProps> = ({
  content,
  color,
}) => {
  if (!content.includes(CONTACT_INFO_PLACEHOLDER)) {
    return (
      <Typography variant="body1" sx={{ color, whiteSpace: 'pre-wrap' }}>
        {content}
      </Typography>
    );
  }

  const segments = content.split(CONTACT_INFO_PLACEHOLDER);

  return (
    <Typography variant="body1" sx={{ color, whiteSpace: 'pre-wrap' }}>
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {segment}
          {index < segments.length - 1 && (
            <Box
              component="span"
              data-testid="redacted-placeholder"
              aria-label="Contact info removed"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.1,
                mx: 0.25,
                borderRadius: 1,
                fontSize: '0.85em',
                fontStyle: 'italic',
                lineHeight: 1.4,
                backgroundColor: 'action.hover',
                color: 'text.secondary',
              }}
            >
              <ShieldOutlinedIcon sx={{ fontSize: '0.95em' }} />
              contact info removed
            </Box>
          )}
        </React.Fragment>
      ))}
    </Typography>
  );
};
